/**
 * A send that fails mid-flight shows its failure line only after the words have landed in the
 * bubble (Gabriel 2026-10-06; offline, the request rejects at once, and a line arriving while
 * the words still fly pushes the bubble up so they land too low). Shared by the admin and client
 * reply hooks.
 *
 * The reply hook expects a landing for the key it sends under; the screen reports when that
 * landing settles (sendLanding resolves when the words are in the bubble, at once under reduced
 * motion); a failure waits for that. A landing that never plays (the bubble is not on screen, the
 * room changed) releases after LANDING_HOLD_MAX_MS. A key with no landing expected (a restored
 * re-post, a tap to retry) holds nothing.
 */
export const LANDING_HOLD_MAX_MS = 1_200;

interface Hold {
  settled: Promise<void>;
  release: () => void;
}

const holds = new Map<string, Hold>();

/** A send under `key` is about to land in its bubble. */
export function expectLanding(key: string): void {
  holds.get(key)?.release();
  let resolve: () => void = () => undefined;
  const settled = new Promise<void>((done) => { resolve = done; });
  let timer: ReturnType<typeof setTimeout> | undefined;
  const hold: Hold = {
    settled,
    release: () => {
      clearTimeout(timer);
      if (holds.get(key) === hold) holds.delete(key);
      resolve();
    },
  };
  holds.set(key, hold);
  timer = setTimeout(hold.release, LANDING_HOLD_MAX_MS);
}

/** The screen's landing for `key`: the hold releases when it settles, or fails. */
export function landingSettled(key: string, landing: Promise<void>): void {
  const hold = holds.get(key);
  if (hold) void landing.then(hold.release, hold.release);
}

/** Resolves once the landing for `key` has settled; at once when none is expected. */
export async function holdUntilLanded(key: string): Promise<void> {
  await holds.get(key)?.settled;
}
